import { Component, Input, Output, EventEmitter, Injector } from '@angular/core';
import { FlowManagerService } from '../../shared/services/flowmanager.service';
import { LogRocketTrackerService } from '../../shared/services/log-rocket-tracker.service';
import { ApplicationActions } from '../../actions/application.actions';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'ola-submit-accordion',
  template: require('./submit-accordion.component.html'),
  styles: [require('./submit-accordion.component.scss')]
})

export class SubmitAccordionComponent {
  @Input() agreements;
  @Output() consent = new EventEmitter();

  personalInfo;
  withholdingTooltipTextNo;
  withholdingTooltipTextYes;
  tinTooltipText;
  tinCertText1;
  tinCertText2;
  tinCertText3;
  tinCertBullet1;
  tinCertBullet2;
  tinCertBullet3;
  tinCertBullet4;
  tinCertBullets;
  tinCertSection3;
  tinName;
  tinTin;
  dejargTIN;
  dejargWithholding;
  backupWithholdingTooltipText;

  constructor(
    flowManager: FlowManagerService,
    logRocketTrackerService: LogRocketTrackerService,
    injector: Injector,
    modal: ModalService,
  ) {
    this.flowManager = flowManager;
    this.modal = modal;
    this.logRocketTrackerService = logRocketTrackerService;
    this.appActions = injector.get(ApplicationActions);

    this.step = 0;
    this.stepMax = 4;
    this.isSsnMasked = true;
    this.backupWithholding = false;
  }

  ngOnInit() {
    this.accountType = this.appActions.getData('ApplicationInformation.accountType');
    if (this.accountType === 'select-custodial') {
      this.personalInfo = this.appActions.getData('CustodialMinorInformation', {});
      this.getCustodialText();
    } else {
      this.personalInfo = { ...this.appActions.getData('YourInformation', {}),
        ...this.appActions.getData('PersonalInformation', {}) };
      this.getDefaultText();
    }
    this.getSharedText();
    this.permanentResident = this.personalInfo.permanentResident;
    this.ssnMasked = this.formatValue(this.personalInfo.ssn, 'ssn');

    const today = new Date();
    const options = { month: 'short', day: '2-digit', year: 'numeric' };
    this.formattedToday = today.toLocaleDateString('en-US', options);

    this.hasEsign = this.flowManager.getEsignOnFile();
    this.isTKA = this.flowManager.getAppType() === 'tka';
    if (this.isTKA && this.agreements.fpslAgreement) { delete this.agreements.fpslAgreement; }
    if (this.hasEsign) { delete(this.agreements.electronicConsent); }
    this.agreementsArr = Object.values(this.agreements)
      .filter(agmt => agmt.showUp === true && agmt.id !== 'esignAgreement')
      .sort((a, b) => {
        return (a.order > b.order) ? 1 : -1;
      });
    // Titles to exclude from the acknowledgement bullet point below agreements
    const excludedAgmtTitles = [
      'esignAgreement',
      'crsAdvisorAgreement',
      'advisorAgreement',
      'adv2Agreement',
      'privacyAgreement',
      'privacyAgreementCA',
      'privacyAgreementApex',
      'onlineServicesAgreement',
      'onlineBusinessContinuityPlan',
    ];
    this.agreementTitles = this.agreementsArr
      .filter(agmt => !excludedAgmtTitles.includes(agmt.id))
      .map((agmt) => {
        if (agmt.id.substring(0, 3) === 'ira') {
          return ` the ${agmt.text}`;
        }
        return ` ${agmt.text}`;
      });
    // capture default withholding
    this.setNotifiedByIRS();
  }

 getCustodialText() {
    this.tinCertText1 = "Federal law requires us to have a record of the minor's ";
    // next are tinDejargText 1&2, tinCertText2, same for minor and non-custodial, thus out of if/else block
    this.tinCertText3 = 'Has the minor been notified by the IRS that he/she is currently subject to backup '
      + 'withholding because he/she failed to report all interest and dividends on his/her tax return?';

    this.tinTooltipText = "This is the minor's Social Security Number or Individual Tax Identification Number "
      + "(ITIN). It should match the first name listed on the custodial "
      + "account and will be used for tax reporting purposes.";

    this.withholdingTooltipTextNo = "No, the minor hasn't been notified.";
    this.withholdingTooltipTextYes = 'Yes, the minor has been notified.';

    // next under the penalties of... and bullets
    this.tinCertBullet1 = "the number shown on this form is the minor's correct taxpayer identification "
      + "number (or I am waiting for the number), and";
    this.tinCertBullet2 = "the minor isn't subject to backup withholding because: (a) the minor's exempt "
      + "from backup withholding, or (b) the minor hasn't been notified by the Internal Revenue Service (IRS) "
      + "that he/she is subject to backup withholding as a result of a failure to report all interest or dividends, "
      + "or (c) the IRS has notified the minor that he/she is no longer subject to backup withholding, and";
    this.tinCertBullet3 = 'the minor is a U.S. citizen or other U.S. person (including a U.S. resident alien), and';
    this.tinCertBullet4 = 'the minor is exempt from Foreign Account Tax Compliance Act reporting.';
    this.tinName = "Minor's name:";
    this.tinTin = "Minor's TIN:";
  }

  getDefaultText() {
    this.tinCertText1 = 'Federal law requires us to have a record of your';
    // next are tinDejargText 1&2, tinCertText2, same for minor and non-custodial, thus out of if/else block
    this.tinCertText3 = "Have you been notified by the IRS that you're currently subject to backup withholding "
      + "because you failed to report all interest and dividends on your tax return?";

    this.tinTooltipText = "For individuals, this is your Social Security Number or Individual Tax "
      + "Identification Number (ITIN). It should match the first "
      + "name listed on the account and will be used for tax reporting purposes.";

    this.withholdingTooltipTextNo = "No, I haven't been notified.";
    this.withholdingTooltipTextYes = "Yes, I've been notified.";

    // next under the penalties of... and bullets
    this.tinCertBullet1 = 'the number shown on this form is my correct taxpayer identification number '
      + '(or I am waiting for a number to be issued to me), and';
    this.tinCertBullet2 = 'I am not subject to backup withholding because: (a) I am exempt from backup withholding, '
      + 'or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup '
      + 'withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified '
      + 'me that I am no longer subject to backup withholding, and';
    this.tinCertBullet3 = 'I am a U.S. citizen or other U.S. person (including a U.S. resident alien), and';
    this.tinCertBullet4 = 'I am exempt from Foreign Account Tax Compliance Act reporting.';
    this.tinName = "Primary applicant's name:";
    this.tinTin = "Primary applicant's TIN:";
  }

  getSharedText() {
    this.dejargTIN = 'Taxpayer Identification Number';
    this.dejargWithholding = 'backup withholding';
    this.tinCertText2 = 'status. If we have missing or incorrect information, we may need to withhold '
      + 'and pay the IRS 24% of the interest earned on your accounts.';

    this.tinCertBullets = 'Under penalties of perjury, I certify that';

    this.backupWithholdingTooltipText = "Backup withholding helps to ensure that government tax collecting agencies "
      + "will be able to receive income taxes owed to them. Backup withholding may be applied if a payee fails to "
      + "provide a correct tax identification number (TIN). The IRS may also notify Ally to begin backup "
      + "withholding due to a customer's underreporting of interest.";
    this.tinCertSection3 = "Ally doesn't collect Foreign Account Tax Compliances Act (FATCA) exemption codes. We're "
      + "required by law to include the aforementioned certifications, but note that number 4 doesn't apply.";
  }

  giveConsent(event) {
    const consentId = event && event.target ? event.target.id : false;
    if (!event) return;

    // skip eSign step if the customer has eSign on file
    if (this.step === 0 && this.hasEsign) {
      this.step++;
    }

    if (this.step < this.stepMax) {
      this.step++;
    }

    this.logRocketTrackerService.track('submit-accordion.component giveConsent() consentId', { consentId });
    this.consent.emit(consentId);
  }

  setNotifiedByIRS() {
    this.appActions.setData('ApplicationInformation.backupWithholding', this.backupWithholding);
  }

  certifyTinNotifiedbyIRS() {
    if (this.step < this.stepMax) {
      this.step++;
    }

    if (this.step === this.stepMax) {
      window.scrollTo(0, 0);
    }
  }

  formatValue(value, formatType) {
    if (formatType === 'ssn' && typeof value === 'string') {
      value = '•••-••-' + value.slice(-4);
    }

    return value;
  }

  maskSSN() {
    this.isSsnMasked = !this.isSsnMasked;
  }

  openModal() {
    this.modal.itinModal(
      "What's a Taxpayer Identification Number?",
      this.tinTooltipText,
      false
    );
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  printPDF(event) {
    event.preventDefault();

    const textTinCertTitle = this.escapeHtml(
      document.getElementById('tinCertTitle')?.innerText
    );

    const tinTooltipText1 = `<span style="text-decoration: underline;"><b>${this.dejargTIN}</b></span>`;
    const tinTooltipText2 = `<span style="text-decoration: underline;"><b>${this.dejargWithholding}</b></span>`;

    let rawSection1Text = `${this.tinCertText1} ${tinTooltipText1} * (TIN) and 
    ${tinTooltipText2} ** ${this.tinCertText2}`;
    rawSection1Text = rawSection1Text.replace(/select to learn more/gi, '');
    const cleanSection1 = rawSection1Text
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const footerContainer = document.querySelector('ns-disclaimer');
    const footerCopyright = document.querySelector('ola-footer .container')?.firstChild;
    const footerCopyrightText = footerCopyright ? this.escapeHtml(footerCopyright?.textContent?.trim()) : '';
    const paragraphs = footerContainer ? footerContainer.querySelectorAll('p') : [];
    const cleanFooterParagraphs = Array.from(paragraphs)
      .map(p => {
        let text = p.innerText;
        text = text
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
        return `<p class="print-footer-p">${this.escapeHtml(text)}</p>`;
      })
      .join('\n');

    const customHtmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${textTinCertTitle}</title>
          <meta charset="utf-8">
          <style>
            @page { size: auto; margin: 0; }
            * { box-sizing: border-box; }
            body { size: letter; margin: 0.9in 0.85in; font-size: 15px; 
            line-height: 1.5; font-family: Arial, Helvetica, sans-serif; 
            color: #000; }
            h2.header { margin-left: auto; margin-right: auto; font-size: 
            22px; font-weight: bold; text-align: center; line-height: 1.3; 
            width: 400px; }
            p { margin: 0 0 14px 0; }
            .print-footer-p { font-size: 11px; line-height: 1.5; }
            .print-footer { font-size: 10px; page-break-before: always; margin-top: 0.9in; }
          </style>
        </head>
        <body>
            <div>
              <h2 class="header">${textTinCertTitle}</h2>
            </div>
            <div>
              <p><b><span style="color: #505050;">${this.tinName}</span></b><br>
              ${this.escapeHtml(this.personalInfo.firstName)} ${this.escapeHtml(this.personalInfo.lastName)}
              </p>
              <p><b><span style="color: #505050;">${this.tinTin}</span></b><br>
              ${this.escapeHtml(this.ssnMasked)}
              </p>
              <p><b><span style="color: #505050;">Date:</span></b><br>
              ${this.formattedToday}
              </p>
              <p>${cleanSection1}</p>
              <p><b><span style="color: #505050;">${this.tinCertText3}</span></b><br>
              [ ${!this.backupWithholding ? "✓" : "&nbsp;&nbsp;&nbsp;"} ] ${this.withholdingTooltipTextNo}<br>
                [ ${this.backupWithholding ? "✓" : "&nbsp;&nbsp;&nbsp;"} ] ${this.withholdingTooltipTextYes}
              </p>
            </div>
            <div>
              <p>${this.tinCertBullets}</p>
                <ol style="padding-left: 0; list-style-position: inside;">
                  <li style="margin-bottom: 5px;">${this.tinCertBullet1}</li>
                  <li style="margin-bottom: 5px;">${this.tinCertBullet2}</li>
                  <li style="margin-bottom: 5px;">${this.tinCertBullet3}</li>
                  <li style="margin-bottom: 5px;">${this.tinCertBullet4}</li>
                </ol>
            </div>
            <div>
              <p>${this.tinCertSection3}</p>
              <p>* ${this.tinTooltipText}</p>
              <p>** ${this.backupWithholdingTooltipText}</p>
            </div>
            <div class="print-footer">
              ${cleanFooterParagraphs}
            </div>
            <div class="print-footer-copyright">
              <p class="print-footer-p">${footerCopyrightText}</p>
            </div>
        </body>
      </html>
    `;

    const printScript = '<script>window.onload=function(){window.print();window.onafterprint=window.close;}</script>';
    const blob = new Blob([customHtmlContent.replace('</body>', printScript + '</body>')], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, '_blank');

    if (!printWindow) {
      URL.revokeObjectURL(blobUrl);
      alert('Popup blocker prevented printing. Please allow popups for this site.');
      return;
    }

    printWindow.onunload = () => URL.revokeObjectURL(blobUrl);
  }
}
